// Git panel: status, configuration, init/commit/push/pull, webhook URL.
import { formatTime, h } from './dom.js';
import { openModal, promptDialog, toast } from './modals.js';
import type { GitConfigInput } from './api.js';
import type { GitStatus } from './types.js';
import type { App } from './app.js';

function statusLine(label: string, value: string, className = ''): HTMLElement {
  return h('div', { class: `stat${className ? ` ${className}` : ''}` }, [
    h('span', { class: 'stat-label', text: label }),
    h('span', { class: 'stat-value', text: value }),
  ]);
}

/** Modal panel that owns every `/api/git/*` interaction. */
export class GitPanel {
  private readonly app: App;

  constructor(app: App) {
    this.app = app;
  }

  open(): void {
    const modal = openModal({ title: 'Git sync', wide: true, kind: 'git' });
    const body = h('div', { class: 'git-body' });
    modal.body.append(body);
    void this.render(body, modal);
  }

  private async render(body: HTMLElement, modal: { close(): void }): Promise<void> {
    body.replaceChildren(h('p', { class: 'empty-note', text: 'Loading git status…' }));
    let status: GitStatus;
    try {
      status = await this.app.api.gitStatus();
    } catch (error) {
      body.replaceChildren(h('p', { class: 'error-text', text: `Git status failed: ${(error as Error).message}` }));
      return;
    }
    const refresh = (): void => void this.render(body, modal);
    body.replaceChildren(this.renderStatus(status), this.renderActions(status, refresh), this.renderConfig(status, refresh), this.renderWebhook(status), this.renderLog(status));
  }

  private renderStatus(status: GitStatus): HTMLElement {
    const rows: HTMLElement[] = [
      statusLine('Repository', status.initialized ? 'initialised' : 'not initialised', status.initialized ? 'ok' : 'warn'),
      statusLine('Enabled', status.enabled ? 'yes' : 'no'),
      statusLine('Remote', status.remote || '—'),
      statusLine('Branch', status.branch || '—'),
      statusLine('Token', status.tokenSet ? 'configured' : 'missing', status.tokenSet ? 'ok' : 'warn'),
      statusLine('Working tree', status.dirty ? 'dirty' : 'clean', status.dirty ? 'warn' : 'ok'),
      statusLine('Ahead / behind', `${status.ahead} / ${status.behind}`),
      statusLine('Auto push', status.autoPush ? 'on' : 'off'),
      statusLine('Pull interval', `${status.pullIntervalMin} min`),
      statusLine('Last push', formatTime(status.lastPushTs)),
      statusLine('Last pull', formatTime(status.lastPullTs)),
    ];
    const section = h('section', { class: 'panel-section git-status' }, [h('h3', { text: 'Status' }), ...rows]);
    if (status.error) section.append(h('p', { class: 'error-text', text: status.error }));
    if (status.lastCommit) {
      section.append(
        h('p', { class: 'muted-text', text: `Last commit ${status.lastCommit.sha.slice(0, 10)} · ${formatTime(status.lastCommit.ts)} · ${status.lastCommit.message}` }),
      );
    }
    return section;
  }

  private renderActions(status: GitStatus, refresh: () => void): HTMLElement {
    const run = async (action: 'init' | 'commit' | 'push' | 'pull', message?: string): Promise<void> => {
      try {
        await this.app.api.gitAction(action, message);
        toast(`git ${action} ok`, { kind: 'success' });
      } catch (error) {
        toast(`git ${action} failed: ${(error as Error).message}`, { kind: 'error' });
      }
      refresh();
    };
    const commit = h('button', {
      class: 'ghost-button',
      text: 'Commit…',
      type: 'button',
      on: {
        click: () => {
          void (async () => {
            const message = await promptDialog({
              title: 'Commit',
              label: 'Commit message',
              value: `vaultsync: manual commit ${new Date().toISOString().slice(0, 16).replace('T', ' ')}`,
              confirmLabel: 'Commit',
            });
            if (message === null) return;
            await run('commit', message);
          })();
        },
      },
    });
    return h('section', { class: 'panel-section' }, [
      h('h3', { text: 'Actions' }),
      h('div', { class: 'button-row' }, [
        h('button', {
          class: 'ghost-button',
          text: status.initialized ? 'Init (re-run)' : 'Init repository',
          type: 'button',
          on: { click: () => void run('init') },
        }),
        commit,
        h('button', { class: 'ghost-button', text: 'Push', type: 'button', on: { click: () => void run('push') } }),
        h('button', { class: 'ghost-button', text: 'Pull', type: 'button', on: { click: () => void run('pull') } }),
      ]),
    ]);
  }

  private renderConfig(status: GitStatus, refresh: () => void): HTMLElement {
    const enabled = h('input', { type: 'checkbox' });
    enabled.checked = status.enabled;
    const autoPush = h('input', { type: 'checkbox' });
    autoPush.checked = status.autoPush;
    const remote = h('input', { class: 'text-input', type: 'text', value: status.remote, placeholder: 'https://github.com/you/vault.git' });
    const branch = h('input', { class: 'text-input', type: 'text', value: status.branch, placeholder: 'main' });
    const token = h('input', {
      class: 'text-input',
      type: 'password',
      value: '',
      placeholder: status.tokenSet ? '•••••• (leave blank to keep)' : 'personal access token',
    });
    const authorName = h('input', { class: 'text-input', type: 'text', value: '', placeholder: 'VaultSync' });
    const authorEmail = h('input', { class: 'text-input', type: 'text', value: '', placeholder: 'vaultsync@example.com' });
    const interval = h('input', { class: 'text-input', type: 'number', value: String(status.pullIntervalMin) });

    const save = h('button', {
      class: 'primary-button',
      text: 'Save git config',
      type: 'button',
      on: {
        click: () => {
          void (async () => {
            const input: GitConfigInput = {
              enabled: enabled.checked,
              remote: remote.value.trim(),
              branch: branch.value.trim(),
              autoPush: autoPush.checked,
              pullIntervalMin: Number(interval.value) || 0,
            };
            // The server never echoes author fields back, so only send them when typed.
            if (token.value.trim()) input.token = token.value.trim();
            if (authorName.value.trim()) input.authorName = authorName.value.trim();
            if (authorEmail.value.trim()) input.authorEmail = authorEmail.value.trim();
            try {
              await this.app.api.saveGitConfig(input);
              toast('Git configuration saved', { kind: 'success' });
              refresh();
            } catch (error) {
              toast(`Save failed: ${(error as Error).message}`, { kind: 'error' });
            }
          })();
        },
      },
    });

    return h('section', { class: 'panel-section' }, [
      h('h3', { text: 'Configuration' }),
      h('div', { class: 'form-grid' }, [
        h('label', { class: 'check-field' }, [enabled, h('span', { text: 'Enable git sync' })]),
        h('label', { class: 'check-field' }, [autoPush, h('span', { text: 'Auto push after commit' })]),
        h('label', { class: 'field' }, [h('span', { text: 'Remote' }), remote]),
        h('label', { class: 'field' }, [h('span', { text: 'Branch' }), branch]),
        h('label', { class: 'field' }, [h('span', { text: 'Access token' }), token]),
        h('label', { class: 'field' }, [h('span', { text: 'Author name' }), authorName]),
        h('label', { class: 'field' }, [h('span', { text: 'Author email' }), authorEmail]),
        h('label', { class: 'field' }, [h('span', { text: 'Pull interval (min)' }), interval]),
      ]),
      h('div', { class: 'button-row' }, [save]),
    ]);
  }

  private renderWebhook(status: GitStatus): HTMLElement {
    const url = `${window.location.origin}/api/git/webhook?secret=${status.webhookSecret}`;
    const code = h('code', { class: 'copy-field', text: url });
    return h('section', { class: 'panel-section' }, [
      h('h3', { text: 'Webhook' }),
      h('div', { class: 'copy-row' }, [
        code,
        h('button', {
          class: 'ghost-button',
          text: 'Copy',
          type: 'button',
          on: {
            click: () => {
              void navigator.clipboard.writeText(url).then(
                () => toast('Webhook URL copied', { kind: 'success' }),
                () => toast('Clipboard unavailable', { kind: 'error' }),
              );
            },
          },
        }),
      ]),
    ]);
  }

  private renderLog(status: GitStatus): HTMLElement {
    const list = h('div', { class: 'history-list' });
    if (status.log.length === 0) list.append(h('p', { class: 'empty-note', text: 'No commits yet.' }));
    for (const commit of status.log.slice(0, 20)) {
      list.append(
        h('div', { class: 'history-row' }, [
          h('div', { class: 'history-head' }, [
            h('span', { class: 'history-rev', text: commit.sha.slice(0, 10) }),
            h('span', { class: 'history-device', text: formatTime(commit.ts) }),
          ]),
          h('div', { class: 'history-meta', text: commit.message }),
        ]),
      );
    }
    return h('section', { class: 'panel-section' }, [h('h3', { text: 'Recent commits' }), list]);
  }
}
